import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, AttachFile } from '@mui/icons-material';
import { listRfqFiles, type RfqFileRow } from '../../../api/rfqFiles';
import { createSignedStorageUrl, downloadStorageObject } from '../../../api/storage';
import { toast } from '../../../utils/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from "../../../api/queryKeys";
import AttachmentCard, { ImagePreviewDialog, type AttachmentFile } from './AttachmentCard';

interface AttachmentViewerProps {
  open: boolean;
  onClose: () => void;
  rfqId: number;
  rfqSubject?: string;
}


const isStored = (file: RfqFileRow): file is AttachmentFile =>
  !!file.storage_path &&
  !!file.storage_bucket &&
  file.storage_path.trim() !== '' &&
  file.storage_bucket.trim() !== '';

const AttachmentViewer: React.FC<AttachmentViewerProps> = ({ 
  open, 
  onClose, 
  rfqId, 
  rfqSubject 
}) => {
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imageUrlsById, setImageUrlsById] = useState<Record<number, string>>({});
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewImageTitle, setPreviewImageTitle] = useState<string>('');
  const queryClient = useQueryClient();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Load attachments when dialog opens
  useEffect(() => {
    if (open && rfqId) {
      loadAttachments();
    }
  }, [open, rfqId]);

  const loadAttachments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: dbError } = await listRfqFiles(rfqId);

      if (dbError) {
        console.error('Error loading attachments:', dbError);
        setError('Failed to load attachments');
        return;
      }

      // Filter out files without valid storage paths and buckets
      const validAttachments = (data || []).filter(isStored);

      if (data && data.length > validAttachments.length) {
        console.warn(`Filtered out ${data.length - validAttachments.length} attachments with missing storage information`);
      }

      setAttachments(validAttachments);
      
      // Invalidate the attachment count cache to ensure it's up to date
      queryClient.invalidateQueries({
        queryKey: queryKeys.attachmentCount(rfqId)
      });
    } catch (err) {
      console.error('Error loading attachments:', err);
      setError('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  const ensureSignedImageUrl = async (attachment: AttachmentFile): Promise<string | null> => {
    try {
      if (imageUrlsById[attachment.id]) {
        return imageUrlsById[attachment.id];
      }
      const { data, error: urlError } = await createSignedStorageUrl(attachment.storage_bucket, attachment.storage_path, 3600);
      if (urlError || !data?.signedUrl) {
        console.warn('Failed to create signed URL for image:', urlError);
        return null;
      }
      setImageUrlsById(prev => ({ ...prev, [attachment.id]: data.signedUrl }));
      return data.signedUrl;
    } catch (e) {
      console.warn('Error generating signed URL:', e);
      return null;
    }
  };

  const handleOpenPreview = async (attachment: AttachmentFile) => {
    const url = await ensureSignedImageUrl(attachment);
    if (url) {
      setPreviewImageUrl(url);
      setPreviewImageTitle(attachment.filename);
    } else {
      toast({ title: 'Preview Unavailable', description: 'Could not load image preview.', severity: 'error' });
    }
  };

  const handleClosePreview = () => {
    setPreviewImageUrl(null);
    setPreviewImageTitle('');
  };

  const downloadAttachment = async (attachment: AttachmentFile) => {
    setDownloadingFile(attachment.filename);
    
    try {
      // Download from Supabase Storage
      const { data: fileData, error: downloadError } = await downloadStorageObject(attachment.storage_bucket, attachment.storage_path);

      if (downloadError) {
        console.error('Download error:', downloadError);
        toast({
          title: "Download Failed",
          description: "Failed to download file from storage",
          severity: "error"
        });
        return;
      }

      // Create download link
      const url = window.URL.createObjectURL(fileData);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Download Started",
        description: `${attachment.filename} download started`,
        severity: "success"
      });

    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download attachment",
        severity: "error"
      });
    } finally {
      setDownloadingFile(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: fullScreen ? undefined : { maxWidth: { md: 900, xl: 1200 } } }}
    >
      <DialogTitle component="div" sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <AttachFile color="action" />
            <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
              Email Attachments
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close attachments">
            <Close />
          </IconButton>
        </Box>
        {rfqSubject && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, wordBreak: 'break-word' }}>
            From: {rfqSubject}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : attachments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AttachFile sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="subtitle1" component="p" color="text.secondary">
              No Attachments Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This RFQ doesn't have any email attachments.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {attachments.length} attachment{attachments.length !== 1 ? 's' : ''} found
            </Typography>
            
            <Grid container spacing={2}>
              {attachments.map((attachment) => (
                <Grid item xs={12} sm={6} md={4} xl={3} key={attachment.id}>
                  <AttachmentCard
                    attachment={attachment}
                    imageUrl={imageUrlsById[attachment.id]}
                    downloading={downloadingFile === attachment.filename}
                    onPreview={() => handleOpenPreview(attachment)}
                    onDownload={() => downloadAttachment(attachment)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Image Preview Dialog */}
      <ImagePreviewDialog url={previewImageUrl} title={previewImageTitle} onClose={handleClosePreview} fullScreen={fullScreen} />
    </Dialog>
  );
};

export default AttachmentViewer; 